import React, { useEffect, useRef, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { brand } from '../../data/projects';
import { firstName } from '../../utils/time';
import type { StudioView } from '../../utils/studioViewHistory';

/**
 * The Crew Directory (formerly "Freelance Database", renamed 8 Sep 2026)
 * lives in its own Magic Patterns design — a separate app entirely, not part
 * of this codebase. "Linking" it means exactly that: an outbound link.
 */
const CREW_DIRECTORY_URL = 'https://project-portal-crew-directory-502.magicpatterns.app';

/**
 * ONE LINE, AND NOTHING IN A MENU. Tom, 26 Sep, in three goes: *"make sure
 * all tabs fit in one line"*, then *"Need wrap up and team to be tabs on the
 * same line - I guess smaller?"*, then *"No I want crew directory on the
 * same line - nothing hidden."*
 *
 * That is the LAPTOP row. Twice I bought room by moving something into the
 * account menu, and twice that was the wrong trade: a tab you cannot see is
 * a tab nobody uses. **Room comes out of the type, not out of the
 * contents.**
 *
 * Where the room came from, measured rather than guessed — ten buttons at
 * 10px with 0.22em tracking came to roughly 1140px against the 1100px the
 * row has at the container's 1180px maximum, so about 40px short:
 *
 * - **Tracking 0.22em → 0.14em** (`tracking-wide2`). At ~86 characters that
 *   is about 70px, and it is the only lever here with that much in it.
 * - **Wordmark h-6 → h-5** on the row, worth another 30-odd px.
 * - px-2.5 and gap-1.5, from the previous pass.
 *
 * **If an eleventh view is ever added, do not shave the type again.** The
 * next notch down stops reading as capitals at all. Either the row becomes
 * two deliberate tiers, or something genuinely leaves it — a decision to
 * put to Tom, not to solve in CSS.
 *
 * `flex-nowrap` is load-bearing: it cannot wrap, so a mistake here shows as
 * overflow rather than as a quiet second row nobody notices for a week.
 *
 * **The full row needs `xl`.** Ten buttons never fit a tablet. From 1280px
 * up the container is pinned at its 1180px maximum, so the row is the same
 * width at every size that shows it. Below that, the Menu sheet and the
 * thumb bar take over.
 *
 * ---
 *
 * **`showTabs` — THE WHOLE RIGHT-HAND SIDE IS GONE ON THE FRONT PAGE
 * (5 Nov 2026).**
 *
 * It began with the tab row. Tom, looking at eight tab names above nine
 * tiles carrying the same eight names: *"we don't need this now do we?"*
 * Then the other two went the same way and for the same reason — *"shouldn't
 * the 'tom' account name go into settings?"* and *"the + NEW Project is an
 * exciting thing to build — any way to make it more special? Maybe it is a
 * tab rather than being stuck up there?"*
 *
 * So on the front page every one of these is now a square in the grid
 * instead, and the header is the wordmark alone. That is not minimalism for
 * its own sake: it is the one screen in the app that cannot scroll, and
 * everything removed here is height the photographs get back.
 *
 * **It is only the front page, and that distinction is the whole of the
 * decision.** The tiles exist nowhere else. Drop this row throughout and a
 * lateral move — Calendar across to Call Sheets — becomes Calendar, home,
 * Call Sheets: a click added to every journey in the app to save one
 * duplicated row on a single screen. The phone's thumb bar is untouched for
 * the same reason, since down there the tiles are two-up and well below the
 * fold.
 */
const NAV_BUTTON = 'inline-flex h-9 shrink-0 items-center whitespace-nowrap border px-2.5 text-[10px] uppercase leading-none tracking-wide2 transition-colors duration-150 ease-out';
const NAV_IDLE = 'border-paper/30 text-paper/70 hover:border-paper hover:text-paper';
const NAV_ACTIVE = 'border-paper text-paper';
const MENU_ITEM = 'label block w-full px-5 py-4 text-left text-paper/60 transition-colors duration-150 ease-out hover:text-paper';

/**
 * WHO SEES DISPATCHES. Owners only — Tom and Zeena. *"Dispatches is only for
 * Zeena and me (owners) you can remove from other staff and future staff."*
 *
 * **Hiding it here secures nothing.** A hidden button is still a function
 * call anyone can make from a console. The actual locks are
 * `is_portal_owner()` on every conversation_* RPC and the matching check
 * inside the ask-dispatches Edge Function. If you ever find yourself relying
 * on this file to keep the studio's correspondence private, stop and go and
 * look at those two.
 */

/**
 * The four things worth a thumb (24 Sep 2026 — rewritten 27 Sep 2026).
 *
 * Tom, 27 Sep: *"for the tabs at the bottom can they be WEEK NEWS DISPATCHES
 * CALENDAR, the rest can be in the menu."*
 *
 * That replaces the set this comment used to argue for — Week, Calendar,
 * Call Sheets, Clients — on the reasoning that the four thumb tabs were the
 * shoot-day tools. **They are not; they are the checking-in tools.** A call
 * sheet and a client are things you open once, on purpose, and a menu is a
 * perfectly good way to open something on purpose. Do not restore the old
 * four on the strength of the old note.
 *
 * **Dispatches is owners only, so Shilpa and Sreyangshi get three tabs.** No
 * fourth was substituted in for them: a different app under the same thumb
 * would be a worse surprise than a shorter bar.
 *
 * **Wrap-ups is deliberately not a fifth thumb tab**: five tabs at 390px is
 * 78px each, and a report written twice a year has no business crushing the
 * four things that are opened daily.
 *
 * Text, not icons: the studio has no icon set and inventing one would be the
 * first colour-and-picture chrome in an app that gets its hierarchy from
 * size and weight alone.
 */
const TABS: {
  view: StudioView;
  label: string;
  ownerOnly?: boolean;
}[] = [{
  view: 'none',
  label: 'Week'
}, {
  view: 'news',
  label: 'News'
}, {
  view: 'conversations',
  label: 'Dispatches',
  ownerOnly: true
}, {
  view: 'calendar',
  label: 'Calendar'
}];
interface Props {
  view: StudioView;
  showingCallSheets: boolean;
  isOwner: boolean;
  name: string;
  /** False on the front page, where the tiles carry all of this. */
  showTabs?: boolean;
  onGoHome: () => void;
  onNewProject: () => void;
  onSelectView: (view: StudioView) => void;
  onOpenTeam: () => void;
  onOpenAccount: () => void;
  onSignOut: () => void;
}
export function StudioNav({
  view,
  showingCallSheets,
  isOwner,
  name,
  showTabs = true,
  onGoHome,
  onNewProject,
  onSelectView,
  onOpenTeam,
  onOpenAccount,
  onSignOut
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Closes on a click elsewhere, or on Escape.
  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);
  return <>
      {/* `shrink-0`: below xl this header is the top row of the shell's fixed
          column (see PortalApp) and must not be squeezed by the scroller.
          `pt-safe`: installed on a home screen the page runs under the status
          bar, or the wordmark sits in the clock. See utils/homeScreen.ts. */}
      <header className="shrink-0 border-b border-ruleDark pt-safe" data-id="element-1561" data-mp="kkyijtq967k5">
        <div className="mx-auto flex max-w-portal items-center justify-between gap-6 px-6 py-4 xl:px-10 xl:py-6" data-id="element-1562" data-mp="pjja74rfviwz">
          {/* The supplied wordmark, in white — proportions untouched. Also the
              way back to the front page from anywhere. */}
          <button type="button" onClick={() => {
          setMenuOpen(false);
          onGoHome();
        }} aria-label="Portal Productions Studio Admin — back to the front page" className="block shrink-0 self-start text-left transition-opacity duration-150 ease-out hover:opacity-60" data-id="element-1563" data-mp="fk0uj0ikajaq">
            <img src={brand.lockup} alt="Portal Productions" className="h-5 w-auto brightness-0 invert" data-id="element-1564" data-mp="h3ua4s78nny3" />
            {/* One line of chrome is one line too many on a 390px screen. */}
            <span className="label-sm m-0 mt-2.5 hidden text-paper/45 xl:block" data-id="element-1565" data-mp="7dsmu2mpm9k8">Studio Admin</span>
          </button>

          {/* Phones, tablets and small laptops: the everything-else door.
              44px tall, per Apple's own minimum. Shown on the front page too
              — the tiles are below the fold down there. */}
          <button type="button" onClick={() => setSheetOpen(true)} aria-haspopup="dialog" aria-expanded={sheetOpen} className="label -mr-2 inline-flex h-11 items-center px-2 text-paper/70 xl:hidden" data-id="element-1566" data-mp="3pjniukvbvv4l">
            Menu
          </button>

          {/* Laptop: the thing you make, then every view in the order they
              get opened, then the account. One line, never wrapping. The
              whole of it is absent on the front page — see `showTabs`. */}
          {showTabs && <div className="hidden flex-nowrap items-center justify-end gap-1.5 xl:flex" data-id="element-1567" data-mp="3pbaevp2i5r0x">
              <button type="button" onClick={onNewProject} className={`${NAV_BUTTON} gap-2 border-paper/30 text-paper/70 hover:border-paper hover:text-paper`} data-id="element-1568" data-mp="2rnrz3uafbonf">
                <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-1569" data-mp="36eioywsggdnw" />
                New Project
              </button>

              <button type="button" onClick={() => onSelectView('news')} aria-current={view === 'news' ? 'page' : undefined} className={`${NAV_BUTTON} ${view === 'news' ? NAV_ACTIVE : NAV_IDLE}`} data-id="element-1570" data-mp="2906fma6w345t">
                News
              </button>

              {/* Owners only — the RPCs and the Edge Function re-check the
                  role server-side. */}
              {isOwner && <button type="button" onClick={() => onSelectView('conversations')} aria-current={view === 'conversations' ? 'page' : undefined} className={`${NAV_BUTTON} ${view === 'conversations' ? NAV_ACTIVE : NAV_IDLE}`} data-id="element-1571" data-mp="3qg2f3fb3n589">
                  Dispatches
                </button>}

              <button type="button" onClick={() => onSelectView('calendar')} aria-current={view === 'calendar' ? 'page' : undefined} className={`${NAV_BUTTON} ${view === 'calendar' ? NAV_ACTIVE : NAV_IDLE}`} data-id="element-1572" data-mp="32ona1erxu9tb">
                Calendar
              </button>

              <button type="button" onClick={() => onSelectView('callSheets')} aria-current={showingCallSheets ? 'page' : undefined} className={`${NAV_BUTTON} ${showingCallSheets ? NAV_ACTIVE : NAV_IDLE}`} data-id="element-1573" data-mp="27hwd62779z73">
                Call Sheets
              </button>

              <button type="button" onClick={() => onSelectView('clients')} aria-current={view === 'clients' ? 'page' : undefined} className={`${NAV_BUTTON} ${view === 'clients' ? NAV_ACTIVE : NAV_IDLE}`} data-id="element-1574" data-mp="1th3h3d192w1v">
                Clients
              </button>

              <button type="button" onClick={() => onSelectView('wrapUps')} aria-current={view === 'wrapUps' ? 'page' : undefined} className={`${NAV_BUTTON} ${view === 'wrapUps' ? NAV_ACTIVE : NAV_IDLE}`} data-id="element-1575" data-mp="1ar3yn8zx0f7f">
                Wrap-ups
              </button>

              {/* Owners only. The Edge Function re-checks the role
                  server-side. */}
              {isOwner && <button type="button" onClick={onOpenTeam} className={`${NAV_BUTTON} ${NAV_IDLE}`} data-id="element-1576" data-mp="w5zs4scbxcws">
                  Team
                </button>}

              {/* Another app, in another tab — but on the row, where it is
                  actually found. */}
              <a href={CREW_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" className={`${NAV_BUTTON} ${NAV_IDLE} no-underline`} data-id="element-1577" data-mp="19wuz1z39pjxq">
                Crew Directory
              </a>

              <div ref={menuRef} className="relative ml-1 shrink-0" data-id="element-1578" data-mp="154tp4jtko5av">
                <button type="button" onClick={() => setMenuOpen(open => !open)} aria-haspopup="menu" aria-expanded={menuOpen} className="inline-flex h-9 items-center gap-2 whitespace-nowrap text-[10px] uppercase leading-none tracking-wide2 text-paper/60 transition-colors duration-150 ease-out hover:text-paper" data-id="element-1579" data-mp="1530ih4iyll2v">
                  {firstName(name)}
                  <span aria-hidden="true" className="text-[0.6rem] leading-none" data-id="element-1580" data-mp="3s0l0khdd0gy6">
                    ▾
                  </span>
                </button>

                {/* Account actions only. Every VIEW is on the row. */}
                {menuOpen && <div role="menu" className="absolute right-0 top-full z-30 mt-4 min-w-[10rem] border border-paper/20 bg-ink" data-id="element-1581" data-mp="2ue42h9wsxzbd">
                    <button type="button" role="menuitem" onClick={() => {
                setMenuOpen(false);
                onOpenAccount();
              }} className={MENU_ITEM} data-id="element-1582" data-mp="38bam16pco21n">
                      Account
                    </button>
                    <button type="button" role="menuitem" onClick={() => {
                setMenuOpen(false);
                onSignOut();
              }} className={`${MENU_ITEM} border-t border-paper/15`} data-id="element-1583" data-mp="1exskntk7acab">
                      Sign Out
                    </button>
                  </div>}
              </div>
            </div>}
        </div>
      </header>

      {sheetOpen && <StudioSheet isOwner={isOwner} name={name} onClose={() => setSheetOpen(false)} onNewProject={onNewProject} onSelectView={onSelectView} onOpenTeam={onOpenTeam} onOpenAccount={onOpenAccount} onSignOut={onSignOut} data-id="element-1584" data-mp="26nfb528rsx6s" />}
    </>;
}

/**
 * Everything that is not a thumb tab, below 1280px.
 *
 * A whole screen rather than a dropdown: a dropdown anchored to the top
 * right of a phone puts every item at the far end of the reach, and these
 * rows are 62px precisely so they can be hit without looking.
 *
 * **27 Sep 2026: Call Sheets and Clients moved in here** when the thumb bar
 * became Week · News · Dispatches · Calendar. They lead the list because
 * they are the two most likely reasons to have opened the menu at all.
 * Nothing that is a thumb tab is repeated here — a menu that lists what is
 * already under your thumb teaches people the menu is where things are.
 */
function StudioSheet({
  isOwner,
  name,
  onClose,
  onNewProject,
  onSelectView,
  onOpenTeam,
  onOpenAccount,
  onSignOut
}: {
  isOwner: boolean;
  name: string;
  onClose: () => void;
  onNewProject: () => void;
  onSelectView: (view: StudioView) => void;
  onOpenTeam: () => void;
  onOpenAccount: () => void;
  onSignOut: () => void;
}) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    /* The page behind must not scroll under the sheet. Harmless below xl,
       where the document no longer scrolls at all. */
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [onClose]);
  const ROW = 'flex h-[62px] w-full items-center border-b border-ruleDark text-left font-display text-xl font-light';
  function go(action: () => void) {
    onClose();
    action();
  }
  return <div role="dialog" aria-modal="true" aria-label="Menu" className="fixed inset-0 z-40 flex flex-col bg-ink pt-safe xl:hidden" data-id="element-1585" data-mp="288cpdqziwsc9">
      <div className="flex items-center justify-between border-b border-ruleDark px-6 py-4" data-id="element-1586" data-mp="6evlmyeikbms">
        <span className="label text-paper/60" data-id="element-1587" data-mp="behxp4ilvlu0">Portal&nbsp;OS</span>
        <button type="button" onClick={onClose} className="label -mr-2 inline-flex h-11 items-center px-2" data-id="element-1588" data-mp="1vwabnazgnnbn">
          Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pt-6" data-id="element-1589" data-mp="1qyijcfuwlz6x">
        {/* The two you open on purpose, first. */}
        <button type="button" onClick={() => go(() => onSelectView('callSheets'))} className={ROW} data-id="element-1590" data-mp="25nn1xy5m25fk">
          Call Sheets
        </button>
        <button type="button" onClick={() => go(() => onSelectView('clients'))} className={ROW} data-id="element-1591" data-mp="2emrzp29cluqo">
          Clients
        </button>
        <button type="button" onClick={() => go(() => onSelectView('wrapUps'))} className={ROW} data-id="element-1592" data-mp="29ogux5xwuafb">
          Wrap-ups
        </button>
        <button type="button" onClick={() => go(onNewProject)} className={ROW} data-id="element-1593" data-mp="2ok6hb570cpkk">
          New Project
        </button>
        {isOwner && <button type="button" onClick={() => go(onOpenTeam)} className={ROW} data-id="element-1594" data-mp="u6nat5v01wn4">
            Team
          </button>}
        <a href={CREW_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" className={`${ROW} justify-between`} data-id="element-1595" data-mp="p8vn6o1e0woq">
          Crew Directory
          <span aria-hidden="true" className="text-sm text-paper/60" data-id="element-1596" data-mp="8czj93o9260z">
            ↗
          </span>
        </a>

        {/* The daily work above, the account below. */}
        <div className="h-9" data-id="element-1597" data-mp="i3kq1ee2uguz" />

        <button type="button" onClick={() => go(onOpenAccount)} className={`${ROW} border-t border-ruleDark text-paper/60`} data-id="element-1598" data-mp="i3uaynk9lxtt">
          Account
        </button>
        <button type="button" onClick={() => go(onSignOut)} className={`${ROW} text-paper/60`} data-id="element-1599" data-mp="3ljhvja1an0mz">
          Sign Out
        </button>
      </div>

      <div className="pb-safe" data-id="element-1600" data-mp="3qlgyiit4fvje">
        <p className="label m-0 px-6 pb-8 pt-5 text-paper/60" data-id="element-1601" data-mp="3ukpmt85e717k">
          {firstName(name)} · Portal Productions
        </p>
      </div>
    </div>;
}

/**
 * The bottom bar, below 1280px — the LAST ROW OF THE SHELL, not a fixed
 * overlay. **Do not make this `position: fixed` again.**
 *
 * It was `fixed inset-x-0 bottom-0` until 27 Sep 2026. On Tom's installed
 * iPhone the document sat at `scrollY = -59` for the life of a session (see
 * utils/viewportPin.ts for why), iOS carries fixed elements with that
 * displacement, and the bar's rect came out 816–911 against an 852-tall
 * screen — its bottom hanging off the screen, and the strip where the labels
 * appear at rest occupied by the Week Ahead day row behind it. Every tab
 * "jumped to the Calendar" because no tab was ever being pressed.
 *
 * The displacement is fixed at its source now, but this stays a laid-out row
 * rather than a positioned overlay: a bar that is part of the column cannot
 * be moved by anything that happens to the document.
 *
 * `showingCallSheets` is still a prop although Call Sheets left the bar: it
 * is what stops Week lighting up while a call sheet is open, that view being
 * reached with `view === 'none'`.
 *
 * **EVEN GAPS, NOT EVEN CELLS (27 Sep 2026).** Tom: *"they don't seem very
 * well spaced out — week and news are further apart from the rest."* Equal
 * cells with centred text produce UNEQUAL gaps whenever the labels differ in
 * length: at 375px each cell was 94px, so WEEK (30px of type) and NEWS
 * (31px) left about 63px of air between them while DISPATCHES (71px) and
 * CALENDAR (57px) left about 30px. The bar read as two pairs rather than one
 * row. Sized to their own type with `justify-between`, the gaps measure 20,
 * 21, 21px, and the first label sits on the page's own 24px gutter.
 *
 * **The trade is that the tap targets are no longer identical**, so `px-3`
 * either side is what keeps them honest: 56–92px wide against a 60px height,
 * all clear of Apple's 44px minimum. Do not tighten it to gain room — shrink
 * the type or drop a tab.
 */
export function StudioTabs({
  view,
  showingCallSheets,
  isOwner,
  onSelectView
}: {
  view: StudioView;
  showingCallSheets: boolean;
  isOwner: boolean;
  onSelectView: (view: StudioView) => void;
}) {
  const tabs = TABS.filter(tab => !tab.ownerOnly || isOwner);
  return <nav aria-label="Portal OS" className="z-20 shrink-0 border-t border-ruleDark bg-ink pb-safe xl:hidden" data-id="element-1602" data-mp="1tf8gc6pwxvn6">
      <div className="flex items-stretch justify-between px-3" data-id="element-1603" data-mp="235bfyprji7uh">
        {tabs.map(tab => {
        const active = !showingCallSheets && view === tab.view;
        return <button key={tab.view} type="button" onClick={() => onSelectView(tab.view)} aria-current={active ? 'page' : undefined} className={`label-sm flex h-[60px] items-center justify-center whitespace-nowrap px-3 ${active ? 'text-paper' : 'text-paper/60'}`} data-id="element-1604" data-mp="286hej47vspbx">
              {tab.label}
            </button>;
      })}
      </div>
    </nav>;
}